import * as THREE from "three";
import { Context } from "../core/context";
import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";
import {
  CommonColor,
  DimensionsAnnotationModifyReturnData,
} from "@/core/Parameters";
import { TextModel } from "@/core/TextModel";
import { LineDimension } from "@/Dimensions/LineDimension";
import { DimensionGroup } from "@/core/DimensionGroup";

class MeasurementModify implements IModifyOperation {
  private m_context: Context;
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_locationObject: THREE.Line;
  private m_tempModelsGroup: THREE.Group = new THREE.Group();
  private m_returnData: DimensionsAnnotationModifyReturnData[] = [];
  private m_points: THREE.Vector3[] = [];
  private m_textModel: TextModel;
  private m_curDistance: string = "";
  private m_dimension: DimensionGroup;
  private m_auxLine: THREE.Line | undefined;
  private m_isStart: boolean = false;
  private m_originScale: number = 1;

  constructor(context: Context) {
    this.m_context = context;
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    this.m_context.SceneManager.AuxModel.add(this.m_tempModelsGroup);
    this.m_textModel = new TextModel(this.m_context);
    this.m_dimension = new DimensionGroup(
      this.m_context.ViewManager,
      this.m_context.SceneManager.AuxModel
    );
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curPoint = point.point;
    this.m_locationObject.position.copy(this.m_curPoint);
    if (this.m_points.length > 0) {
      this.m_dimension.Line.SetDimension(
        this.m_points[0],
        this.m_curPoint,
        this.scale
      );
      this.m_curDistance = (
        this.m_points[0].distanceTo(this.m_curPoint) * 1000
      ).toFixed(2);
      this.m_dimension.Line.SetVaule(this.m_curDistance);
      this.m_auxLine?.geometry.setFromPoints([
        this.m_points[0],
        this.m_curPoint,
      ]);
      this.m_auxLine?.computeLineDistances();
      this.m_dimension.Line.Input.Visibility = true;
    }
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    this.m_originScale = this.scale * 5;
    if (!this.m_points.length) {
      if (!this.m_isStart) {
        this.m_auxLine = this.m_context.ToolsManager.CreatedAuxLine();
        this.m_isStart = true;
      }
    }
    this.m_points.push(point.point);
    if (this.m_points.length == 2) {
      this.createTempObject();
      this.m_dimension.Line.Input.Visibility = false;
      this.m_dimension.Line.SetDimension(this.m_points[0], this.m_points[0], 0);
      this.m_points.length = 0;
    }
  }
  onKeyDown(e: KeyboardEvent): void {}
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    this.clear();
    if (operate == ModifyOperateState.complete) {
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e?: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  onMouseUp(e: PointerEvent): void {
    if (e.button !== 0) return;
  }

  private createTempObject() {
    let baseLine = this.m_context.ToolsManager.CreatedAuxLine();
    let baseGeo = baseLine.geometry;
    let baseVector = new THREE.Vector3().subVectors(
      this.m_curPoint,
      this.m_points[0]
    );
    baseGeo.setFromPoints([this.m_points[0], this.m_curPoint]);
    baseLine.frustumCulled = false;
    baseLine.computeLineDistances();
    this.m_tempModelsGroup.add(baseLine);
    let baseText = this.m_textModel.CreateText({
      text: this.m_curDistance,
      size: this.m_originScale * 3,
      color: CommonColor.Aux,
    });

    let assist1 = this.m_context.ToolsManager.CreatedAuxLine(CommonColor.Red);
    let assistGeo1 = assist1.geometry;
    let length1 = baseVector.clone().dot(new THREE.Vector3(1, 0, 0));
    let assist1DirVector = new THREE.Vector3(Number(length1), 0, 0);
    let assist1EndPoint = this.m_points[0].clone().add(assist1DirVector);
    assistGeo1.setFromPoints([this.m_points[0], assist1EndPoint]);
    assist1.frustumCulled = false;
    assist1.computeLineDistances();
    this.m_tempModelsGroup.add(assist1);
    let assist1TextNumber = Math.abs(Number(length1) * 1000).toFixed(2);

    let assist1Text = this.m_textModel.CreateText({
      text: assist1TextNumber,
      size: this.m_originScale * 3,
      color: CommonColor.Red,
    });
    let subText1 = Number(this.m_curDistance) - Number(assist1TextNumber);
    if (subText1 < 0.01 || subText1 == Number(this.m_curDistance)) {
      assist1Text.visible = false;
      assist1.visible = false;
    }

    let assist2 = this.m_context.ToolsManager.CreatedAuxLine(CommonColor.Green);
    let assistGeo2 = assist2.geometry;
    assistGeo2.setFromPoints([this.m_curPoint, assist1EndPoint]);
    assist2.frustumCulled = false;
    assist2.computeLineDistances();
    this.m_tempModelsGroup.add(assist2);
    let assist2TextNumber = Math.abs(
      this.m_curPoint.distanceTo(assist1EndPoint) * 1000
    ).toFixed(2);
    let assist2Text = this.m_textModel.CreateText({
      text: assist2TextNumber,
      size: this.m_originScale * 3,
      color: CommonColor.Green,
    });
    let subText2 = Number(this.m_curDistance) - Number(assist2TextNumber);
    if (subText2 < 0.01 || subText2 == Number(this.m_curDistance)) {
      assist2Text.visible = false;
      assist2.visible = false;
    }

    let baseTextMatrix = this.getTextMatrix(
      this.m_points[0].clone(),
      this.m_curPoint.clone()
    );
    baseText.applyMatrix4(baseTextMatrix);
    this.m_tempModelsGroup.add(baseText);

    let assist1TextMatrix = this.getTextMatrix(
      assist1EndPoint.clone(),
      this.m_points[0].clone()
    );
    assist1Text.applyMatrix4(assist1TextMatrix);
    this.m_tempModelsGroup.add(assist1Text);

    let assist2TextMatrix = this.getTextMatrix(
      this.m_curPoint.clone(),
      assist1EndPoint.clone()
    );
    assist2Text.applyMatrix4(assist2TextMatrix);
    this.m_tempModelsGroup.add(assist2Text);
  }

  private clear() {
    this.m_context.ToolsManager.GroupClear(this.m_tempModelsGroup);
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_auxLine!,
    ]);
    this.m_dimension?.dispose();
  }

  private get scale() {
    return this.m_context.ViewManager.ScreenModelScale;
  }
  private getTextMatrix(startP: THREE.Vector3, endP: THREE.Vector3) {
    let xAxis = new THREE.Vector3(1, 0, 0);
    let zAxis = new THREE.Vector3(0, 0, 1);
    let vector = endP
      .clone()
      .sub(startP)
      .normalize()
      .applyAxisAngle(zAxis, -Math.PI / 2)
      .setLength(this.m_originScale * 2.5);
    let CenterP = startP.clone().add(endP).multiplyScalar(0.5);
    let v = endP.clone().sub(startP).normalize();
    let angle = v.angleTo(xAxis);
    if (v.clone().cross(xAxis).z > 0) {
      angle = -angle;
    }
    if (Math.abs(angle) > Math.PI / 2) {
      angle = angle - Math.PI;
    }

    let textP = CenterP.clone().add(vector);

    let q = new THREE.Quaternion().setFromAxisAngle(zAxis, angle);
    let s = new THREE.Vector3(1, 1, 1);
    return new THREE.Matrix4().compose(textP, q, s);
  }
}

export { MeasurementModify };
